// packages/core/src/learning/schools/first-100k.ts
// ─────────────────────────────────────────────────────────────────────────
// Authored exhibit for "First $100k". Matches boglehead-indexing.ts gold
// standard.
//
// ID-bound fields (the validator + compiler guard these):
//   - figures:        FigureId[]  — held [] for the post-authoring cross-ref
//                     pass. Worldview names: charlie-munger, grant-sabatier,
//                     jl-collins.
//   - applyActions:   ApplyAction union — type-checked at compile time. Only
//                     live/allowed action types emitted.
//   - featuredLessonIds / debateIds — [] until the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const FIRST_100K_EXHIBIT: SchoolExhibitEntry = {
	id: 'first-100k',
	tagline: 'First $100k believes the first real pile changes your behavior.',
	worldviewSummary:
		'First $100k is paying attention to the painful early stretch where progress feels slow, boring, and easy to quit. This lens protects you from dismissing small gains before they become visible momentum. It accepts a real tradeoff: the early phase often requires patience, repetition, and delayed upgrades before the payoff feels obvious. The blind spot is that the milestone can become a status scoreboard instead of a stability marker. At its best, this lens teaches you that the first serious base changes how you see risk, work, spending, and future freedom.',
	coreBeliefs: [
		'First $100k believes the early pile matters because it changes your confidence.',
		'First $100k believes momentum is built before it looks impressive.',
		'First $100k believes the first serious base makes future growth feel less abstract.',
		'First $100k believes income, savings rate, and behavior all matter during the climb.',
		'First $100k believes early sacrifice should create options, not become your whole identity.',
		'First $100k believes the milestone is useful because it proves the system is working.',
	],
	warnsAgainst: [
		'Quitting because the first stage feels too slow.',
		'Spending every improvement before it becomes a foundation.',
		'Measuring yourself against people who started with different income, help, luck, or obligations.',
		'Turning the first $100,000 into a vanity target instead of a freedom base.',
	],
	helps: 'This lens helps people who need a concrete early milestone that makes wealth building feel real. It may not fit when the number becomes shame, comparison, or an excuse to ignore health, relationships, debt stress, or basic stability.',
	figures: [],
	canon: [
		{
			title: 'The first $100,000 idea',
			oneLineIdea:
				'The first serious base can feel hardest because habits, income, and compounding are still getting traction.',
		},
		{
			title: 'The Simple Path to Wealth',
			author: 'JL Collins',
			oneLineIdea:
				'A simple money system can help early accumulation become less fragile.',
		},
		{
			title: 'Financial Freedom',
			author: 'Grant Sabatier',
			oneLineIdea:
				'Early money momentum comes from combining earning power, saving behavior, and intentional investing.',
		},
		{
			title: 'The snowball effect',
			oneLineIdea:
				'The early stage is slow, but a growing base can make later progress feel less dependent on effort alone.',
		},
		{
			title: 'Behavioral momentum',
			oneLineIdea:
				'The milestone matters partly because it teaches you that your system can actually work.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-first-100k',
	debateIds: [],
	applyActions: [
		{
			label: 'See how your current gap builds toward the first $100,000',
			action: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
		},
		{
			label: 'Look at the base you already have',
			action: {
				type: 'openDashboard',
				label: 'Open your Dashboard',
			},
		},
		{
			label: 'Test how extra monthly savings changes the climb',
			action: {
				type: 'openSimulator',
				label: 'Simulate more monthly savings',
				scenarioType: 'aggressive-savings',
			},
		},
		{
			label: 'Write what the first $100,000 would change for you',
			action: {
				type: 'noToolAction',
				label: 'Name the change',
				prompt: 'Write what reaching the first $100,000 would change about your stress, choices, work, or confidence.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'The first $100,000 is not magic. It is proof that your system works.',
			kind: 'hook',
		},
		{
			line: 'The hidden cost of quitting early is never seeing momentum start to compound.',
			kind: 'hook',
		},
		{
			line: 'What did your first serious money milestone change about how you saw yourself?',
			kind: 'interview-question',
		},
		{
			line: 'Ask someone what felt harder: making the first $10,000 or keeping the habits after it.',
			kind: 'creator-prompt',
		},
		{
			line: 'Nisk helps users understand the milestone, then test the climb on their own numbers.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show your audience why the first $100,000 is more about behavior than flexing.',
			kind: 'creator-prompt',
		},
		{
			line: 'What does the first $100,000 get right, and when does it become toxic comparison?',
			kind: 'interview-question',
		},
		{
			line: 'Before money feels powerful, it usually feels boring.',
			kind: 'hook',
		},
	],
	revision: 1,
};
